"use client";
import style from "@/styles/ServiceForm.module.scss";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Image from "next/image";
import OrangeSpan from "@/Ui_Elements/OrangeSpan/OrangeSpan";
import CloudflareCaptcha from "@/Components/CloudflareCaptcha";

import FloatingField from "@/Ui_Elements/FloatingField/FloatingField";
import { getValidationSchema } from "@/lib/validationSchemas.js";
import { CommonSubmitForm } from "@/lib/CommonSubmitForm";
import { yupResolver } from "@hookform/resolvers/yup";

const ServiceForm = () => {
  const validationSchema = getValidationSchema(2);
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
    reset,
    setError, // <-- added
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const [isValidNum, setIsValidNum] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  // Cloudflare captcha callback
  const handleVerify = (token) => {
    setCaptchaToken(token || null);

    if (token) {
      setValue("captcha", token, {
        shouldValidate: true,
        shouldDirty: true,
      });
      clearErrors("captcha");
    } else {
      setValue("captcha", null);
    }
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    clearErrors("captcha");

    // 🔥 Force remount Turnstile
    setCaptchaKey((k) => k + 1);
  };

  // Phone input change callback (from your FloatingField / intl input)
  const onPhoneChange = (isValid, rawValue) => {
    const containsOnlyNumbers = /^\d+$/.test(rawValue);

    if (containsOnlyNumbers || !rawValue) {
      setIsValidNum(isValid);
      setPhoneNumber(rawValue);
      setValue("mobile", rawValue, { shouldValidate: true });
    }
  };

  // Final submit handler
  const onSubmit = async (data, e) => {
    const isValidForm = await trigger();
    // check captcha
    if (!captchaToken) {
      setError("captcha", { type: "manual", message: "Captcha verification failed" });
      return;
    }
    if (isValidForm && isValidNum && captchaToken) {
      const res = await CommonSubmitForm(2, data);
      if (res.success) {
        reset();
        setPhoneNumber("");
        setValue("captcha", null);
        resetCaptcha();
      }
    }
  };

  return (
    <section className={`${style["contactusformmaindiv"]}`}>
      <div className="container">
        <div className={`${style["newpartfull"]}`}>
          <div className={`${style["sectionheading"]}`}>
            <OrangeSpan text={`Contact Us`} classname={`${style["badge"]}`} />
            <h2 className={`${style["commonsectiontitle"]} commonsectiontitle`}>{`Transform Your Vision into Reality – Let’s Digitize Your Future!`}</h2>
          </div>
          <div className={`${style["contactformbody"]}`}>
            <div className={`${style["left"]}`}>
              <div className={`${style["lefttop"]} ${style["bgcard"]}  ${style["contentpart"]}`}>
                <div className={`${style["imagebox"]} `}>
                  <Image src={"/images/contact_us.png"} alt="image" width={0} height={0} />
                </div>
              </div>
            </div>
            <div className={`${style["right"]} ${style["bgcard"]}  ${style["bgcardpattrent"]} `}>
              <form id="service-form" className={`${style["formmain"]} commonformclass service-form commonform`} onSubmit={handleSubmit(onSubmit)} autoComplete="on">
                <h3 className={`${style["formheading"]}`}>How can I Help You?</h3>
                {/* <p className={`${style["formsubheading"]}`}>
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s,
                </p> */}
                <div className={`${style["inputgroup"]} inputgroup ${style["w-100"]}`}>
                  <FloatingField
                    id="name_serviceform"
                    type="text"
                    label="Your Name"
                    placeholder="Enter your name"
                    name="name"
                    register={register}
                    error={errors.name}
                    setValue={setValue}
                    isFloatedLabel={false}
                  />
                </div>
                <div className={`${style["inputgroup"]} inputgroup ${style["w-100"]}`}>
                  <FloatingField
                    id="email_serviceform"
                    type="email"
                    label="Your Email"
                    placeholder="Enter your email"
                    name="email"
                    register={register}
                    error={errors.email}
                    setValue={setValue}
                    isFloatedLabel={false}
                  />
                </div>
                <div className={`${style["inputgroup"]} inputgroup ${style["w-50"]}`}>
                  <FloatingField
                    id="mobile_serviceform"
                    type="intl"
                    label="Phone no."
                    placeholder="Phone no."
                    name="mobile"
                    register={register}
                    phoneNumber={phoneNumber}
                    isValidNum={isValidNum}
                    onPhoneNumberChange={onPhoneChange}
                    error={errors.mobile}
                    setValue={setValue}
                    isFloatedLabel={false}
                  />
                </div>
                <div className={`${style["inputgroup"]} inputgroup ${style["w-50"]}`}>
                  <FloatingField
                    id="organization_serviceform"
                    type="text"
                    label="Institute"
                    placeholder="Institute Name"
                    name="organization"
                    register={register}
                    error={errors.organization}
                    setValue={setValue}
                    isFloatedLabel={false}
                  />
                </div>
                <div className={`${style["inputgroup"]} inputgroup ${style["w-100"]}`}>
                  <FloatingField
                    id="message_serviceform"
                    type="text"
                    label="Message"
                    placeholder="Enter your message"
                    name="message"
                    register={register}
                    error={errors.message}
                    textarea={true}
                    setValue={setValue}
                    isFloatedLabel={false}
                  />
                </div>
                <div className={`${style["inputgroup"]} inputgroup ${style["w-50"]}`}>
                  <div className={`${style["elevate-recaptcha"]} c-recaptcha`}>
                    <CloudflareCaptcha onVerify={handleVerify} key={captchaKey} />
                    {errors.captcha && <div className={`${style["error"]} captcha-error`}>{errors.captcha.message}</div>}
                    <input
                      type="hidden"
                      {...register("captcha", {
                        required: "Captcha verification failed",
                      })}
                    />
                  </div>
                </div>
                {/* Add other form fields here */}
                <div className={`${style["formbottompart"]}`}>
                  <button className={`${style["buttonsubmit"]} commonformbtn`} type="submit">
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default ServiceForm;
